/* eslint-disable no-throw-literal */
import axios from 'axios';
import _ from 'lodash';
import * as O from 'fp-ts/lib/Option';
import * as B from 'fp-ts/lib/boolean';
import { pipe } from 'fp-ts/lib/function';
import endpoints from 'src/constants/endpoints';
import ErrorHandler from './ErrorHandler';
import { getCookieTokens } from '@schemeserve/global-utils';

/**
 * The SchemeServe REST xhr handler. Wrapping axios & allowing endpoint selection
 * by name. We can also cleanly format uri variables & query parameters.
 *
 * Another advantage is that we no longer have to use try/catch or .then to catch
 * errors as we do not reject the promise after axios returns, so we actually just
 * resolve the error message in stead & and fail silently.
 * XHR will retun an object of either the data, or the error.
 *
 * Using this method of returning the data we can then define our
 * error logic underneath the await method with no callback craziness.
 *
 * @category API
 * @example <caption>Simple 'get' request</caption>
 * const { data, error } = await xhr.get('endpointName')
 * @example <caption>With query param, uri variable & payload</caption>
 * const { data, error } = await xhr.post({
 *   endpoint: 'siteData',
 *   params: { foo: 'bar', test: undefined },
 *   uri: { id: 1 },
 *   payload: { foo: 'bar' },
 * })
 */
export class XHR {
  /**
   *
   * @param {string|xhrData} param1 - The endpoint name or @type {xhrData} object
   * @param {xhrData} [param2=Object] - Additional options & data for the request
   * @param {string} operation - The REST operation for axios to perform
   */
  constructor(param1, param2, operation) {
    this.operation = operation;
    // Data can be either first or 2nd parameter
    this.data = typeof param1 === 'string' ? param2 || {} : param1;
    // Data endpoint could be first param as string, or as property of first param if object
    this.endpoint = typeof param1 === 'string' ? param1 : param1.endpoint;
    // We could also have an external url with no endpoint name
    if (this.data && this.data.url) {
      this.endpoint = this.data.url;
    }
  }

  /**
   * @method
   * @returns {Promise} Promise object represents the axios call
   */
  getXHRResult = () => {
    if (this.data.fetchAll) {
      return this.fetchAllPaginatedItems();
    }
    return this.axiosXHRAction();
  };

  updateUrlWithDevRoot = () => {
    const apiRoot = this.url.split('/')[0];
    const foundDevRoute = Object.keys(window.devRoutes).find((route) => {
      let routeName = route.split('Api')[0];
      routeName = routeName.toLowerCase();
      return routeName === apiRoot;
    });

    this.url = `${
      foundDevRoute
        ? window.devRoutes[foundDevRoute]
        : window.ENVIRONMENT?.ApiRoot?.Base ?? 'https://api.dev.schemeserve.com'
    }/${foundDevRoute ? this.url.split(apiRoot)[1] : this.url}`;
    this.url = this.url.replace(/([^:]\/)\/+/g, '$1');
  };

  /**
   * Format the data
   * @method
   */
  formatData = () => {
    const { uri = {}, params = {} } = this.data;
    this.url = endpoints?.[this.endpoint] ?? this.endpoint;

    if (!this.url) {
      throw new Error('No url provided to xhr handler');
    }

    if (Object.keys(uri).length) {
      this.url = this.getReplacedUri();
    }

    if (Object.keys(params).length) {
      this.url = this.addQueryParams();
    }

    if (Object.keys(window?.devRoutes ?? {}).length && !process.env.API_KEY) {
      this.updateUrlWithDevRoot();
    } else {
      this.url = `${window.ENVIRONMENT?.ApiRoot?.Base ?? 'https://api.dev.schemeserve.com'}/${this.url}`;
    }
  };

  removeReactErrorOverlay = () => {
    let count = 0;
    const interval = setInterval(() => {
      count += 1;
      if (count > 1000) {
        clearInterval(interval);
      }
      if (document.body.lastChild.nodeName === 'IFRAME') {
        clearInterval(interval);
        document.body.lastChild.style.display = 'none';
      }
    }, 1);
  };

  /**
   * Perform axios operation, and resolve with the response.
   * @method
   * @returns {Promise} Promise object represents the axios call
   */
  axiosXHRAction = () => {
    const { onError, isCritical, errorText } = this.data;
    return new Promise((resolve) => {
      const authorizationToken = pipe(
        getCookieTokens(),
        O.map((tokens) => tokens.accessToken),
        O.map((token) => `bearer ${token}`),
        O.map((token) => ({ Authorization: token })),
        O.getOrElse(() => ({})),
      );

      const siteIdHeader = pipe(
        this.endpoint === 'site',
        B.match(
          () => ({}),
          () => ({ 'site-id': process.env.SITEID }),
        ),
      );
      axios({
        method: this.operation,
        url: this.url,
        data: this.data.payload,
        headers: {
          ...authorizationToken,
          ...siteIdHeader,
        },
        endpointName: this.endpoint,
        errorText,
      })
        .then((res) => {
          resolve({
            data: res.data,
            status: res.status,
            statusText: res.statusText,
            headers: res.headers,
          });
        })
        .catch((error) => {
          if (onError) {
            onError(error);
            return;
          }
          if (isCritical) {
            if (process.env.NODE_ENV === 'development') {
              this.removeReactErrorOverlay();
            }
            ErrorHandler.updateBreakingErrors({
              [window.lastClickedReactModule]: true,
            });
          }
          if (errorText) {
            return;
          }

          resolve({
            status: error.response && error.response.status,
            statusText: error.response && error.response.statusText,
            message: error.message,
            error,
            headers: error.response && error.response.headers,
          });
        });
    });
  };

  /**
   * Keep fetching from given endpoint until all items are aquired.
   * Adjust the skip each time it fetches.
   * @method
   * @returns {Promise} Promise object the data obtained from continuous fetching.
   */
  fetchAllPaginatedItems = async () =>
    new Promise((resolve) => {
      let skip = 0;
      let loopCount = 0;
      let items = [];
      const { params = {} } = this.data;
      let hasParams = Object.keys(params).length;

      if (!params.skip) {
        if (hasParams) {
          this.url = `${this.url}&Skip=0`;
        } else {
          this.url = `${this.url}?Skip=0`;
        }
        hasParams = true;
      }
      if (!params.size) {
        if (hasParams) {
          this.url = `${this.url}&Size=50`;
        } else {
          this.url = `${this.url}?Size=50`;
        }
      }

      const updateUrl = () => {
        this.url = this.url
          .replace(/Skip=\d+/, `Skip=${skip}`)
          .replace(/Size=\d+/, `Size=50`);
      };

      const getAllItems = async () => {
        if (loopCount > 10) {
          return;
        }

        updateUrl();

        const { data = { items: [] } } = await this.axiosXHRAction();

        loopCount += 1;
        skip += 50;
        items = [...items, ...data.items];

        if (data.items.length && items.length < data.totalItemCount) {
          await getAllItems();
        }

        resolve({ data: { items } });
      };

      getAllItems();
    });

  /**
   * Construct a query string and return the url with string attached.
   * Omit any params that are falsy.
   * @method
   */
  addQueryParams = () => {
    const updatedParams = `${
      this.data.params
        ? Object.keys(
            _.pickBy(this.data.params, (param) => param || param === 0),
          ).reduce(
            (acc, param, index) =>
              `${acc}${index > 0 ? '&' : ''}${param}=${
                this.data.params[param]
              }`,
            '?',
          )
        : ''
    }`;

    if (
      this.data.trailingSlash &&
      this.url.charAt(this.url.length - 1) !== '/'
    ) {
      return `${this.url}/${updatedParams}`;
    }
    return `${this.url}${updatedParams}`;
  };

  /**
   * Add the uri parts onto the url.
   * Parts are defined by their property names &
   * the placeholder names to match in the endpoints constant.
   *
   * @method
   */
  getReplacedUri = () =>
    this.url
      .split(/\{(.*?)\}/)
      .map((part) => {
        if (part.includes('/') || part.includes('\\') || part.startsWith(':')) {
          return part;
        }

        return this.data.uri[part];
      })
      .join('');

  /**
   * @memberof XHR
   * @method
   * @param {string|xhrData} endpoint
   * @param {xhrData} [data=Object]
   */
  static get = (endpoint, data = {}) => {
    const instance = new XHR(endpoint, data, 'get');

    instance.formatData();
    return instance.getXHRResult();
  };

  /**
   * @memberof XHR
   * @method
   * @param {string|xhrData} endpoint
   * @param {xhrData} [data=Object]
   */
  static post = (endpoint, data = {}) => {
    const instance = new XHR(endpoint, data, 'post');

    instance.formatData();
    return instance.getXHRResult();
  };

  /**
   * @memberof XHR
   * @method
   * @param {string|xhrData} endpoint
   * @param {xhrData} [data=Object]
   */
  static patch = (endpoint, data = {}) => {
    const instance = new XHR(endpoint, data, 'patch');

    instance.formatData();
    return instance.getXHRResult();
  };

  /**
   * @memberof XHR
   * @method
   * @param {string|xhrData} endpoint
   * @param {xhrData} [data=Object]
   */
  static delete = (endpoint, data = {}) => {
    const instance = new XHR(endpoint, data, 'delete');

    instance.formatData();
    return instance.getXHRResult();
  };

  /**
   * @memberof XHR
   * @method
   * @param {string|xhrData} endpoint
   * @param {xhrData} [data=Object]
   */
  static put = (endpoint, data = {}) => {
    const instance = new XHR(endpoint, data, 'put');

    instance.formatData();
    return instance.getXHRResult();
  };
}

export default XHR;
